import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import { CircularProgress, Typography } from '@mui/material'
import BannerImage from '../../components/banner/banner-image'
import ContentContainer from '../../areas/layout/content-container'
import {
  StyledPageWrapper,
  StyledLoadingWrapper,
  StyledBannerContent,
  StyledPoster as DetailPoster,
  StyledPosterFallback,
  StyledTitle,
  StyledOverview,
  StyledRecommendationsSection,
  StyledRecommendationsTitle,
  StyledCardScroller,
} from '../../components/Details/details-page-styles'
import moviesApi from '../../client/movies-api'
import { CollectionDetailDTO } from '../../types/Movie'
import LazyImage from '../../components/lazy-image'
import LargeCard from '../../components/cards/large-card'
import { styled } from '@mui/material/styles'
import { CollectionNotFound, LoadError } from '../Status/status-page'

const CollectionInfo = styled('div')(({ theme }) => ({
  textAlign: 'left',
  [theme.breakpoints.down('md')]: {
    textAlign: 'center',
  },
}))

const CollectionTitle = styled(StyledTitle)(({ theme }) => ({
  fontSize: 42,
  [theme.breakpoints.down('md')]: {
    fontSize: 32,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 28,
  },
}))

const CollectionOverview = styled(StyledOverview)({
  marginBottom: 0,
})

const CollectionDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const [collection, setCollection] = useState<CollectionDetailDTO | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<number | null>(null)

  const load = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError(null)
    setStatus(null)
    try {
      const res = await moviesApi.getCollection(Number(id))
      setCollection(res.data)
    } catch (err) {
      setStatus(axios.isAxiosError(err) ? (err.response?.status ?? null) : null)
      setError('Failed to load collection')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    load()
  }, [load])

  if (loading) {
    return (
      <StyledLoadingWrapper>
        <CircularProgress />
      </StyledLoadingWrapper>
    )
  }

  if (error || !collection) {
    return status && status !== 404 ? (
      <LoadError onRetry={load} />
    ) : (
      <CollectionNotFound />
    )
  }

  return (
    <StyledPageWrapper>
      <BannerImage imageUrl={collection.backdropUrl || collection.posterUrl}>
        <ContentContainer>
          <StyledBannerContent>
            <DetailPoster>
              <LazyImage
                src={collection.posterUrl}
                alt={collection.name}
                fallback={
                  <StyledPosterFallback>{collection.name}</StyledPosterFallback>
                }
              />
            </DetailPoster>

            <CollectionInfo>
              <CollectionTitle>{collection.name}</CollectionTitle>
              {collection.overview && (
                <CollectionOverview>{collection.overview}</CollectionOverview>
              )}
            </CollectionInfo>
          </StyledBannerContent>
        </ContentContainer>
      </BannerImage>

      <ContentContainer>
        <StyledRecommendationsSection>
          {collection.parts.length === 0 ? (
            <Typography color='text.secondary'>
              No titles in this collection
            </Typography>
          ) : (
            <React.Fragment>
              <StyledRecommendationsTitle>
                Explore the collection
              </StyledRecommendationsTitle>
              <StyledCardScroller>
                {collection.parts.map((movie) => (
                  <LargeCard
                    key={movie.id}
                    id={movie.id}
                    type='movie'
                    title={movie.title}
                    year={movie.year}
                    rating={movie.rating}
                    posterUrl={movie.posterUrl}
                    genres={movie.genres}
                  />
                ))}
              </StyledCardScroller>
            </React.Fragment>
          )}
        </StyledRecommendationsSection>
      </ContentContainer>
    </StyledPageWrapper>
  )
}

export default CollectionDetailPage
